import { sendRequest } from '@infrastructure/api/api';
import { DistrictListResponse, DistrictQueryParams, District } from '../types/district';
import ApiResponse from '@core/types/apiResponse';
import { mapPaginatedToSingle } from '@core/utils/mapPaginatedToSingle';

/**
 * Fetches a list of districts based on query parameters
 * @param params - Query parameters for filtering and pagination
 * @returns Promise<ApiResponse<DistrictListResponse>>
 */
export const fetchDistricts = async (params: DistrictQueryParams = {}): Promise<ApiResponse<DistrictListResponse>> => {
  const queryString = new URLSearchParams();
  
  // Add parameters to query string
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryString.append(key, typeof value === 'boolean' ? (value ? '1' : '0') : String(value));
    }
  });

  return await sendRequest<any, DistrictListResponse>('GET', `district/query?${queryString}`);
};

/**
 * Fetches a single district by ID
 * @param id - District ID
 * @returns Promise<ApiResponse<District>>
 */
export const fetchDistrictById = async (id: number): Promise<ApiResponse<District>> => {
  const listResponse = await fetchDistricts({ districtId: id, page: 1, perPage: 1 });
  return mapPaginatedToSingle<District>(listResponse, 'District not found');
};

/**
 * Creates a new district
 * @param nationId - The ID of the nation
 * @param districtData - District creation data
 * @returns Promise<ApiResponse<District>>
 */
export const createDistrict = async (nationId: number, districtData: { name: string; description: string }): Promise<ApiResponse<District>> => {
  return await sendRequest<any, District>('POST', `nation/${nationId}/district/create`, districtData);
};

/**
 * Updates an existing district
 * @param nationId - The ID of the nation
 * @param districtId - The ID of the district to update
 * @param districtData - District update data
 * @returns Promise<ApiResponse<any>>
 */
export const updateDistrict = async (nationId: number, districtId: number, districtData: { name: string; description: string }): Promise<ApiResponse<any>> => {
  return await sendRequest<any, any>('POST', `nation/${nationId}/district/${districtId}/update`, districtData);
};

/** Disables a district (sets it inactive). */
export const disableDistrict = async (nationId: number, districtId: number): Promise<ApiResponse<{ message: string }>> => {
  return await sendRequest<never, { message: string }>('POST', `nation/${nationId}/district/${districtId}/disable`);
};

/** Enables a district (sets it active). */
export const enableDistrict = async (nationId: number, districtId: number): Promise<ApiResponse<{ message: string }>> => {
  return await sendRequest<never, { message: string }>('POST', `nation/${nationId}/district/${districtId}/enable`);
};

/** Set or clear a district head church from nation scope. */
export const updateNationDistrictHeadChurch = async (
  nationId: number,
  districtId: number,
  headChurchId: number | null
): Promise<ApiResponse<{ message: string }>> => {
  return await sendRequest<{ head_church_id: number | null }, { message: string }>(
    'POST',
    `nation/${nationId}/district/${districtId}/head-church/update`,
    { head_church_id: headChurchId }
  );
};

/** Set or clear a district head church from district scope. */
export const updateDistrictHeadChurch = async (
  districtId: number,
  headChurchId: number | null
): Promise<ApiResponse<{ message: string }>> => {
  return await sendRequest<{ head_church_id: number | null }, { message: string }>(
    'POST',
    `district/${districtId}/head-church/update`,
    { head_church_id: headChurchId }
  );
};
